/**
 * reportsService
 *
 * Data-fetching layer for the Reports section. Mirrors journalArticlesService
 * but targets the GET /api/biblio/reports endpoint, which returns only biblio
 * sources with sourceType = "report".
 *
 * Report rows live in the same DynamoDB table as journal articles, so this
 * module reuses the JournalArticle type for the returned items.
 */

import { JournalArticle } from "@/services/journalArticlesService"

/**
 * The report types stored on report rows, in sidebar order.
 *
 * Keep in sync with the backend REPORT_TYPES in
 * modules/biblio/lambdas/biblio/handler.py and the classifier GROUPS in
 * scripts/biblio_classify_report_sources/biblio_classify_report_sources.py.
 */
export const REPORT_TYPES = [
  "globalReport",
  "plan",
  "householdSurvey",
  "surveillance",
  "healthStatistics",
  "institutionalReport",
  "research",
] as const

/** One of the REPORT_TYPES. */
export type ReportType = (typeof REPORT_TYPES)[number]

/**
 * The API's pseudo reportType filter value selecting reports with no
 * reportType: placeholders, test records and misfiled sources the classifier
 * marks as needs review, plus reports it has not classified yet. Not a stored
 * value. The frontend shows these reports as "Needs Review".
 */
export const REPORT_TYPE_UNCLASSIFIED = "unclassified"

/** A value the API accepts in its reportType filter. */
export type ReportTypeFilterValue = ReportType | typeof REPORT_TYPE_UNCLASSIFIED

/** Label for reports with no reportType, in the sidebar, page title and badges. */
export const NEEDS_REVIEW_LABEL: string = "Needs Review"

/**
 * Display label for each report type.
 *
 * Typed as a Record over ReportType so the compiler flags a value added to
 * REPORT_TYPES without a label.
 */
export const reportTypeLabelMap: Record<ReportType, string> = {
  globalReport: "Global & Multilateral",
  plan: "Programme Plans",
  householdSurvey: "Household Surveys",
  surveillance: "Surveillance",
  healthStatistics: "Health Statistics",
  institutionalReport: "Institutional Reports",
  research: "Research",
}

/**
 * Check whether a string is one of the stored report types.
 *
 * @param value - The string to test.
 * @returns True (and narrows the type) when `value` is a ReportType.
 */
function isReportType(value: string): value is ReportType {
  // `includes` on a readonly tuple of literals only accepts those literals, so
  // widen the tuple to readonly string[] for the membership check.
  return (REPORT_TYPES as readonly string[]).includes(value)
}

/**
 * Get the display label for a source's reportType.
 *
 * A report without a reportType (undefined or empty) is "Needs Review".
 *
 * @param reportType - The source's reportType field, or undefined when absent.
 * @returns The label from reportTypeLabelMap, or NEEDS_REVIEW_LABEL.
 * @throws Error when reportType is a non-empty value missing from
 *   reportTypeLabelMap. That means the backend has a type the frontend does
 *   not know about and the label map needs updating; failing loudly makes the
 *   mismatch visible instead of rendering a blank or misleading cell.
 */
export function getReportTypeLabel(reportType: string | undefined): string {
  if (!reportType) {
    return NEEDS_REVIEW_LABEL
  }
  if (!isReportType(reportType)) {
    throw new Error(
      `Unknown reportType "${reportType}": add it to REPORT_TYPES and reportTypeLabelMap`
    )
  }
  return reportTypeLabelMap[reportType]
}

/**
 * Optional filter parameters for the reports API. Each one is sent as a query
 * parameter only when supplied (and non-empty).
 */
export interface ReportsFilters {
  /** Comma-separated ReportTypeFilterValues (e.g. "plan,unclassified"); OR logic. */
  reportType?: string
  /**
   * Search term. An all-digit term looks up a source id; any other text
   * matches title, citation, and report number.
   */
  searchTerm?: string
  /** YYYY-MM-DD lower bound (inclusive) on createdDate. */
  createdAfter?: string
  /** Comma-separated prExtractionStatus API values (e.g. "NOT_STARTED,EXTRACTED"). */
  prExtractionStatus?: string
  /** Comma-separated rsExtractionStatus API values (e.g. "NOT_STARTED,EXTRACTED"). */
  rsExtractionStatus?: string
  /** Field to sort by. When omitted, results are newest first by createdDate. */
  sortBy?: "publicationYear"
  /** Sort direction; only applies when sortBy is provided. */
  sortDirection?: "asc" | "desc"
}

/**
 * Response shape from the paginated reports API endpoint
 * (GET /api/biblio/reports). The API also echoes the applied filters; those
 * fields are not used by the frontend and are not typed here.
 */
export interface ReportsResponse {
  /** The page of report records. */
  items: JournalArticle[]
  /** Total number of reports matching the filters. */
  totalCount: number
  /** The 1-based page number returned. */
  page: number
  /** Number of items per page. */
  pageSize: number
  /** Total number of pages for the current filters and page size. */
  totalPages: number
}

/**
 * Error thrown by getReports on a non-2xx response, carrying the HTTP status
 * so callers can tell, for example, a 403 (user not in an allowed biblio group)
 * from a 400 (bad filter).
 */
export class ReportsApiError extends Error {
  /** The HTTP status code of the failed response. */
  readonly status: number

  /**
   * @param message - Human-readable error message (the API's `error` field when present).
   * @param status - HTTP status code of the response.
   */
  constructor(message: string, status: number) {
    super(message)
    this.name = "ReportsApiError"
    this.status = status
  }
}

// Cached reports API URL loaded from aws-exports.json. Module state, so tests
// reset it through resetReportsApiUrlCacheForTests.
let REPORTS_API_URL = ""

/**
 * Clear the cached reports API URL so the next getReports call reloads
 * /aws-exports.json. Test-only: production code never needs to call this.
 *
 * @returns Nothing.
 */
export function resetReportsApiUrlCacheForTests(): void {
  REPORTS_API_URL = ""
}

/**
 * Load the reports API URL from aws-exports.json configuration, caching it for
 * later calls. Mirrors the loader in journalArticlesService so both endpoints
 * resolve their base URL the same way.
 *
 * @returns The full URL for the reports API endpoint.
 * @throws Error if aws-exports.json cannot be fetched or parsed.
 */
async function loadApiUrl(): Promise<string> {
  if (REPORTS_API_URL) {
    return REPORTS_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    // VITE_API_URL env var takes precedence (used with npm run dev --host).
    // Falls back to apiUrl from aws-exports.json (CI-generated for deployed envs).
    // Last resort: relative path (will 404 locally but works behind CloudFront).
    const baseUrl = import.meta.env.VITE_API_URL || config.apiUrl || ""
    REPORTS_API_URL = baseUrl ? `${baseUrl}/api/biblio/reports` : "/api/biblio/reports"
    return REPORTS_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("reports API URL not configured")
  }
}

/**
 * Fetch a page of reports from the GET /api/biblio/reports endpoint.
 *
 * `page` and `pageSize` are always sent; every other filter is sent only when
 * supplied. The request is routed through CloudFront to the API Gateway. Like
 * the journal articles endpoint, it is available to all users: the biblio
 * Lambda only requires membership of map-apps-admins or map-apps-general-users.
 *
 * @param idToken - Auth0 access token for API authentication.
 * @param page - 1-based page number.
 * @param pageSize - Number of items per page (max 200, enforced by the API).
 * @param filters - Optional filters (see ReportsFilters).
 * @returns The page of reports with totalCount, page, pageSize, and totalPages.
 * @throws ReportsApiError on a non-2xx response, with the API's `error`
 *   message (or the HTTP status when the body has none) and the status code.
 */
export async function getReports(
  idToken: string,
  page: number,
  pageSize: number,
  filters?: ReportsFilters
): Promise<ReportsResponse> {
  const apiUrl = await loadApiUrl()

  // Build the query string: pagination always, filters only when supplied.
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("pageSize", String(pageSize))

  if (filters?.reportType) {
    params.set("reportType", filters.reportType)
  }
  if (filters?.searchTerm) {
    params.set("searchTerm", filters.searchTerm)
  }
  if (filters?.createdAfter) {
    params.set("createdAfter", filters.createdAfter)
  }
  if (filters?.prExtractionStatus) {
    params.set("prExtractionStatus", filters.prExtractionStatus)
  }
  if (filters?.rsExtractionStatus) {
    params.set("rsExtractionStatus", filters.rsExtractionStatus)
  }
  if (filters?.sortBy) {
    params.set("sortBy", filters.sortBy)
  }
  if (filters?.sortDirection) {
    params.set("sortDirection", filters.sortDirection)
  }

  const response = await fetch(`${apiUrl}?${params.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  if (!response.ok) {
    // The error body is JSON ({ "error": "..." }) for API-generated errors, but
    // gateway or CloudFront errors may not be JSON, so a parse failure yields
    // an empty object and the message falls back to the HTTP status.
    const errorData: { error?: unknown } = await response.json().catch(() => ({}))
    const statusText: string = response.statusText ? ` ${response.statusText}` : ""
    const message: string =
      typeof errorData.error === "string" && errorData.error
        ? errorData.error
        : `HTTP error! status: ${response.status}${statusText}`
    throw new ReportsApiError(message, response.status)
  }

  const data: ReportsResponse = await response.json()
  return data
}
